/* eslint-disable @typescript-eslint/no-use-before-define */
import axios from 'axios';
import Button from 'components/UI_kit_3/Button/Button';
import FormTextMasked from 'components/UI_kit_3/FormText/FormTextMasked';
import { useFormik } from 'formik';
import { useAppSelector, useUserThunkDispatch } from 'hooks';
import useAsyncFetch from 'hooks/async-fetch';
import React from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { userActions } from 'store/user';
import { User } from 'store/user/types';
import { IOtpAuthDto } from 'store/user/types/api-bodies.types';
import { UserRole } from 'store/user/types/state.types';
import { ReactComponent as BackIcon } from 'assets/icons_3/arrows/arrow-left.svg';
import * as yup from 'yup';

const OtpFrom: React.FC = () => {
  const thunkUserDispatch = useUserThunkDispatch();
  const [t, i18n] = useTranslation();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const dispatch = useDispatch();

  const handleLogOut = () => {
    dispatch(userActions.clearUser());
    navigate('/');
  };

  const schema = React.useMemo(() => yup.object().shape({
    totpKey: yup.string().required(t('form.error.required')).length(6, t('form.error.required')),
  }), [t]);

  const [handleLoginConfirm, loading] = useAsyncFetch({
    asyncFunc: async (dto: IOtpAuthDto) => {
      const res = await thunkUserDispatch(userActions.signInConfirm(dto, i18n.language));
      return res;
    },
    successHandler: (res) => {
      if (res instanceof User) {
        if (res.role === UserRole.Customer) {
          navigate('services', { replace: true });
        }
        if (res.role === UserRole.Admin) {
          navigate('admin', { replace: true });
        }
      }
    },
    errorHandler: ({ error, errMessage }) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 403) {
          setFieldError('totpKey', t('two_fa.wrong_otp'));
        } else {
          // something wrong with server on no connection
          setFieldError('totpKey', errMessage);
        }
      }
    },
  });

  const {
    handleSubmit,
    setFieldValue,
    dirty,
    values,
    touched,
    errors,
    setFieldError,
  } = useFormik({
    validationSchema: schema,
    onSubmit: handleLoginConfirm,
    initialValues: {
      login: user.tempLogin || '',
      password: user.tempPass || '',
      totpKey: '',
    },
    validateOnBlur: true,
  });

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <div className="mb-4xl text-md-regular text-color-tertiary">
        {t('two_fa.note_contact_support')}
      </div>
      <FormTextMasked
        value={values.totpKey}
        className="mb-3xl"
        id="otp"
        label={t('two_fa.otp_long')}
        isInvalid={touched.totpKey && !!errors.totpKey}
        name="totpKey"
        type="text"
        mask="000 000"
        unmask
        autoFocus
        error={errors.totpKey}
        onAccept={(value) => setFieldValue('totpKey', value)}
        qa-element="totp-key-mask"
      />
      <Button
        className="w-100 mb-3xl"
        disabled={loading || !dirty || !!errors.totpKey}
        type="submit"
        size="lg"
        variant="primary"
        qa-element="otp-submit"
      >
        {t('common.submit')}
      </Button>
      <Button
        className="ml-auto mr-auto"
        disabled={loading}
        variant="link-gray"
        size="lg"
        prefixIcon={<BackIcon />}
        onClick={handleLogOut}
        qa-element="logout"
      >
        {t('auth.signout')}
      </Button>
    </Form>
  );
};
export default OtpFrom;
